"use client";

import { Crown, Sparkles } from "lucide-react";
import { useTranslations } from "next-intl";
import { useState } from "react";

import { useSession } from "@/app/hooks/useSession";
import { PremiumModal } from "@/components/premium/PremiumModal";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface AiCreditLimitBannerProps {
  className?: string;
  /** Inside a card already (Prep ahead): no surface of its own, so there is no card in a card. */
  plain?: boolean;
}

/**
 * The weekly AI limit, the same in every AI spot on web and iOS (Jeremy, 2026-10-10): centered on its own soft card, a
 * sparkle, the headline and one line under it. Free accounts get an outlined "Upgrade to Premium" with the crown in the
 * accent (the Settings "Get Premium" style), so it never competes with the screen's filled main button; premium accounts
 * are told when the credits come back.
 */
export function AiCreditLimitBanner({ className, plain = false }: AiCreditLimitBannerProps) {
  const t = useTranslations("recipeChat");
  const { isPremium } = useSession();
  const [showPremiumModal, setShowPremiumModal] = useState(false);

  return (
    <>
      <div
        className={cn(
          "flex flex-col items-center px-5 py-6 text-center",
          !plain && "rounded-2xl bg-muted",
          className
        )}
      >
        <div className="mb-3 flex size-12 items-center justify-center rounded-full bg-primary/15 text-primary">
          <Sparkles className="size-6" aria-hidden />
        </div>
        <p className="font-semibold text-foreground">{t("limitReached")}</p>
        <p className="mt-1.5 max-w-xs text-sm text-muted-foreground">
          {isPremium ? t("limitReachedPremium") : t("limitReachedFree")}
        </p>
        {!isPremium && (
          <Button
            variant="outline"
            className="mt-4 gap-2 rounded-full"
            onClick={() => setShowPremiumModal(true)}
          >
            <Crown className="size-4 text-primary" aria-hidden />
            {t("upgradeToPremium")}
          </Button>
        )}
      </div>

      <PremiumModal
        isOpen={showPremiumModal}
        onClose={() => setShowPremiumModal(false)}
        limitType="ai"
      />
    </>
  );
}
